/**
 * <s-hover-zoom> — magnifies product photos under the cursor.
 *
 * Wraps the gallery track. Frames marked [data-hover-zoom] scale their image
 * and follow the pointer so shoppers can inspect fabric and print up close.
 * Only for fine pointers that can hover (mouse / trackpad), and off when the
 * shopper prefers reduced motion. Clicking still opens the full-screen zoom.
 */
class SHoverZoom extends HTMLElement {
  #ac;
  #frame = null;
  #raf = 0;
  static SCALE = 2;

  connectedCallback() {
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!canHover || reduceMotion) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal, passive: true };
    this.addEventListener('pointermove', this.#onMove, opts);
    this.addEventListener('pointerleave', () => this.#reset(), opts);
    this.querySelector('[data-track]')?.addEventListener('scroll', () => this.#reset(), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#raf);
  }

  #onMove = (event) => {
    const frame = event.target instanceof Element ? event.target.closest('[data-hover-zoom]') : null;
    if (frame !== this.#frame) this.#reset();
    if (!frame) return;
    this.#frame = frame;

    const { clientX, clientY } = event;
    cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => {
      const image = frame.querySelector('img');
      if (!image) return;
      const rect = frame.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
      const y = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));
      image.style.transformOrigin = `${x}% ${y}%`;
      image.style.transform = `scale(${SHoverZoom.SCALE})`;
    });
  };

  #reset() {
    cancelAnimationFrame(this.#raf);
    const image = this.#frame?.querySelector('img');
    if (image) {
      image.style.transform = '';
      image.style.transformOrigin = '';
    }
    this.#frame = null;
  }
}

if (!customElements.get('s-hover-zoom')) {
  customElements.define('s-hover-zoom', SHoverZoom);
}
